/* ════════════════════════════════════════════════════════════════════
   src/api/client.js
   THE one place that gets swapped when the real backend is ready.

   Today:    reads JSON files from /public/data/
   Tomorrow: change USE_MOCK to false and set API_BASE — done.
   ════════════════════════════════════════════════════════════════════ */

import * as mock from './mock.js';

// const USE_MOCK = true; https://portal.1ayurved.com/1ayurvedapi/public
// const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.1ayurved.com';

const USE_MOCK = false;
const API_BASE = import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000';

/**
 * Generic GET helper. Returns parsed JSON.
 * In production, attaches JWT from localStorage.
 * Throws on non-2xx so React Query can catch it.
 * On 401, clears session and redirects to /login.
 */
async function realGet(path) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (res.status === 401) {
    logout();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login?expired=1';
    }
    throw new Error('Session expired');
  }
  if (!res.ok) {
    const error = new Error(`API ${res.status} for ${path}`);
    error.status = res.status;
    throw error;
  }
  return res.json();
}

/* ──────────────────────────── Audit log ──────────────────────────── */

export async function fetchAuditPage(params = {}) {
  if (USE_MOCK) return mock.fetchAuditPage(params);
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/audit?${q.toString()}`);
}

/** Full filtered audit trail (no pagination) for Excel export — Super Admin only. */
export async function fetchAuditExport(params = {}) {
  if (USE_MOCK) return mock.fetchAuditPage({ ...params, limit: 10000 });
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/audit/export?${q.toString()}`);
}

export async function fetchAuditSummary() {
  if (USE_MOCK) return mock.fetchAuditSummary();
  return realGet('/api/v1/audit/summary');
}

export async function fetchAuditFilterOptions() {
  if (USE_MOCK) return mock.fetchAuditFilterOptions();
  return realGet('/api/v1/audit/filter-options');
}

/* ──────────────────────────── Clinics ──────────────────────────── */

export async function fetchClinicsPage(params = {}) {
  if (USE_MOCK) return mock.fetchClinicsPage(params);
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/clinics?${q.toString()}`);
}

export async function fetchClinicsSummary() {
  if (USE_MOCK) return mock.fetchClinicsSummary();
  return realGet('/api/v1/clinics/summary');
}

export async function fetchClinicsFilterOptions() {
  if (USE_MOCK) return mock.fetchClinicsFilterOptions();
  return realGet('/api/v1/clinics/filter-options');
}

/* ──────────────────────────── Doctors ──────────────────────────── */

export async function fetchDoctorsPage(params = {}) {
  if (USE_MOCK) return mock.fetchDoctorsPage(params);
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/doctors?${q.toString()}`);
}

export async function fetchDoctorsSummary() {
  if (USE_MOCK) return mock.fetchDoctorsSummary();
  return realGet('/api/v1/doctors/summary');
}

export async function fetchDoctorsFilterOptions() {
  if (USE_MOCK) return mock.fetchDoctorsFilterOptions();
  return realGet('/api/v1/doctors/filter-options');
}

/* ──────────────────────────── Staff ──────────────────────────── */

export async function fetchStaffPage(params = {}) {
  if (USE_MOCK) return mock.fetchStaffPage(params);
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/staff?${q.toString()}`);
}

export async function fetchStaffSummary() {
  if (USE_MOCK) return mock.fetchStaffSummary();
  return realGet('/api/v1/staff/summary');
}

export async function fetchStaffFilterOptions() {
  if (USE_MOCK) return mock.fetchStaffFilterOptions();
  return realGet('/api/v1/staff/filter-options');
}

/* ──────────────────────────── Onboarding ──────────────────────────── */

// Map an onboarding `kind` to its REST resource segment. Most kinds are just
// the plural (doctor→doctors), but "staff" is already plural — the backend
// routes are /staff/..., so appending an "s" (→ "staffs") 404s every fetch,
// create, save and activate, silently losing all staff onboarding edits.
function onboardingResource(kind) {
  return kind === 'staff' ? 'staff' : `${kind}s`;
}

export async function fetchOnboardingSchema(kind) {
  if (USE_MOCK) return mock.fetchOnboardingSchema(kind);
  return realGet(`/api/v1/schemas/${kind}-onboarding`);
}

export async function fetchOnboardingData(kind, id) {
  if (USE_MOCK) return mock.fetchOnboardingData(kind, id);
  return realGet(`/api/v1/${onboardingResource(kind)}/${id}/onboarding`);
}

export async function createOnboarding(kind, sectionId, values, opts) {
  if (USE_MOCK) return mock.createOnboarding(kind, sectionId, values, opts);
  const token = localStorage.getItem('1ayurved_token');
  // Merge the scope (e.g. { clinicId }) into values so the create endpoint can
  // stamp the correct clinic. Values from the form win over the scope.
  const merged = { ...(opts || {}), ...(values || {}) };
  const res = await fetch(`${API_BASE}/api/v1/${onboardingResource(kind)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ sectionId, values: merged }),
  });
  if (!res.ok) {
    // Surface the server's reason (e.g. "A clinic is required to add a doctor")
    // so the form shows something actionable instead of a bare status code.
    let msg = `Could not create ${kind} (${res.status})`;
    try { const j = await res.json(); if (j && j.message) msg = j.message; } catch { /* non-JSON */ }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function activateOnboarding(kind, id) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/${onboardingResource(kind)}/${id}/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    // Surface the server's reason (e.g. the clinic-activation checklist) so the
    // form can show exactly what still needs doing, not a bare status code.
    let msg = `Could not activate (${res.status})`;
    try { const j = await res.json(); if (j && j.message) msg = j.message; } catch { /* non-JSON body */ }
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

export async function saveOnboardingSection(kind, id, sectionId, values) {
  if (USE_MOCK) return mock.saveOnboardingSection(kind, id, sectionId, values);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/${onboardingResource(kind)}/${id}/sections/${sectionId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),    },
    body: JSON.stringify(values),
  });
  if (!res.ok) {
    const err = new Error(`API ${res.status} for save ${kind}/${id}/${sectionId}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/* ──────────────────────────── Reports ──────────────────────────── */

export async function fetchReports(period) {
  if (USE_MOCK) return mock.fetchReports(period);
  const q = period ? `?period=${encodeURIComponent(period)}` : '';
  return realGet(`/api/v1/reports/cross-clinic${q}`);
}

/* ──────────────────────────── Compliance ──────────────────────────── */

export async function fetchCompliance(year) {
  if (USE_MOCK) return mock.fetchCompliance(year);
  const q = year ? `?year=${encodeURIComponent(year)}` : '';
  return realGet(`/api/v1/compliance${q}`);
}

/* ──────────────────────────── Master forms ──────────────────────────── */

export async function fetchMasterForms() {
  if (USE_MOCK) return mock.fetchMasterForms();
  return realGet('/api/v1/master-forms');
}

export async function saveMasterFormsCategory(category, subKey, items) {
  if (USE_MOCK) return mock.saveMasterFormsCategory(category, subKey, items);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(
    `${API_BASE}/api/v1/master-forms/${category}/${subKey}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json', Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ items }),
    }
  );
  if (!res.ok) {
    const err = new Error(`API ${res.status} for save ${category}/${subKey}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/* ──────────────────────────── Procedures ──────────────────────────── */

export async function fetchProcedures() {
  if (USE_MOCK) return mock.fetchProcedures();
  return realGet('/api/v1/procedures');
}

export async function saveProcedures(subKey, payload) {
  if (USE_MOCK) return mock.saveProcedures(subKey, payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/procedures/${subKey}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ values: payload }),
  });
  if (!res.ok) {
    const err = new Error(`API ${res.status} for save procedures/${subKey}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Best-effort flush of a pending procedures edit while the page is unloading
 * (refresh / tab close). A normal fetch is aborted when the document unloads,
 * so any autosave still in its debounce window would be lost — `keepalive`
 * lets the request outlive the page. Fire-and-forget; never throws.
 */
export function saveProceduresKeepalive(subKey, payload) {
  if (USE_MOCK) return;
  try {
    const token = localStorage.getItem('1ayurved_token');
    fetch(`${API_BASE}/api/v1/procedures/${subKey}`, {
      method: 'PUT',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json', Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ values: payload }),
    }).catch(() => {});
  } catch { /* unload path — nothing more we can do */ }
}

/* ──────────────────────────── Lab reference ──────────────────────────── */

export async function fetchLabReference() {
  if (USE_MOCK) return mock.fetchLabReference();
  return realGet('/api/v1/lab-reference');
}

export async function saveLabReferenceCategory(category, payload) {
  if (USE_MOCK) return mock.saveLabReferenceCategory(category, payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/lab-reference/${category}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ values: payload }),
  });
  if (!res.ok) {
    const err = new Error(`API ${res.status} for save lab-reference/${category}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/* ──────────────────────────── Drugs ──────────────────────────── */

export async function fetchDrugsSummary() {
  if (USE_MOCK) return mock.fetchDrugsSummary();
  return realGet('/api/v1/drugs/summary');
}

export async function fetchDrugsFilterOptions() {
  if (USE_MOCK) return mock.fetchDrugsFilterOptions();
  return realGet('/api/v1/drugs/filter-options');
}

export async function fetchDrugsPage(params = {}) {
  if (USE_MOCK) return mock.fetchDrugsPage(params);
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') qs.set(k, String(v));
  }
  return realGet(`/api/v1/drugs?${qs.toString()}`);
}

/* ──────────────────────────── Data correction ──────────────────────────── */

export async function fetchDataCorrection() {
  if (USE_MOCK) return mock.fetchDataCorrection();
  return realGet('/api/v1/data-correction');
}

export async function decideDataCorrection(tab, id, decision) {
  if (USE_MOCK) return mock.decideDataCorrection(tab, id, decision);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/data-correction/${tab}/${id}/decide`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(decision),
  });
  if (!res.ok) {
    const err = new Error(`API ${res.status} for data-correction decide`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/* ──────────────────────────── Auth ──────────────────────────── */

const TOKEN_KEY = '1ayurved_token';
const USER_KEY  = '1ayurved_user';

/**
 * Request an OTP for a phone number. Backend sends OTP via MSG91.
 * @param {string} phone  E.164 format e.g. "+919000000001"
 * @returns {Promise<{challengeId: string, expiresIn: number}>}
 */
export async function requestOtp(phone) {
  if (USE_MOCK) return mock.requestOtp(phone);
  const res = await fetch(`${API_BASE}/api/v1/auth/otp/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  });
  if (!res.ok) {
    const err = new Error(`OTP request failed: ${res.status}`);
    err.status = res.status;
    err.body = await res.json().catch(() => null);
    throw err;
  }
  return res.json();
}

/**
 * Verify the OTP. On success, persists token + user to localStorage.
 * @param {string} challengeId
 * @param {string} otp  6 digits
 * @returns {Promise<{token, user, expiresAt}>}
 */
export async function verifyOtp(challengeId, otp) {
  if (USE_MOCK) {
    const result = await mock.verifyOtp(challengeId, otp);
    persistSession(result);
    return result;
  }
  const res = await fetch(`${API_BASE}/api/v1/auth/otp/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ challengeId, otp }),
  });
  if (!res.ok) {
    const err = new Error(`OTP verify failed: ${res.status}`);
    err.status = res.status;
    err.body = await res.json().catch(() => null);
    throw err;
  }
  const result = await res.json();
  persistSession(result);
  return result;
}

/**
 * Practo-style self sign-up — step 1: request an OTP for a brand-new phone.
 * Unlike requestOtp, an unknown number is allowed (that's the point); an
 * already-registered one comes back 409 so the UI can send them to sign-in.
 */
export async function signupRequest(phone) {
  const res = await fetch(`${API_BASE}/api/v1/auth/signup/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ phone }),
  });
  if (!res.ok) {
    const err = new Error(`Signup request failed: ${res.status}`);
    err.status = res.status;
    err.body = await res.json().catch(() => null);
    throw err;
  }
  return res.json();
}

/**
 * Self sign-up — step 2: verify OTP + name, create the account, sign in.
 * On success, persists token + user exactly like verifyOtp.
 */
export async function signupVerify({ challengeId, otp, name, email }) {
  const res = await fetch(`${API_BASE}/api/v1/auth/signup/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ challengeId, otp, name, email }),
  });
  if (!res.ok) {
    const err = new Error(`Signup verify failed: ${res.status}`);
    err.status = res.status;
    err.body = await res.json().catch(() => null);
    throw err;
  }
  const result = await res.json();
  persistSession(result);
  return result;
}

/** Clear local session — does NOT call the backend (no server-side session). */
export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // Drop any dual-role view choice so the next login on this browser starts
  // in its own primary role, never a previous user's switched view.
  localStorage.removeItem('1ayurved_active_role');
}

/**
 * Re-fetch the logged-in user from the server and refresh the cached copy.
 *
 * getCurrentUser() only reads the snapshot persisted at login, so a change
 * made server-side afterwards (e.g. a doctor being assigned Clinic Admin, which
 * adds 'clinicadmin' to their roles) never reaches the client until they log
 * out and back in. Calling this on app load keeps user.roles current, which is
 * what the header role switcher reads. Falls back to the cached user on any
 * network hiccup; on 401 it clears the session like realGet does.
 */
export async function fetchMe() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return getCurrentUser();
  try {
    const res = await fetch(`${API_BASE}/api/v1/me`, {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      logout();
      if (window.location.pathname !== '/login') window.location.href = '/login?expired=1';
      return null;
    }
    if (!res.ok) return getCurrentUser();
    const fresh = await res.json();
    localStorage.setItem(USER_KEY, JSON.stringify(fresh));
    return fresh;
  } catch {
    return getCurrentUser();
  }
}

/** Get the currently logged-in user from localStorage, or null. */
export function getCurrentUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Has a token? Doesn't validate, just checks presence. */
export function isAuthenticated() {
  return !!localStorage.getItem(TOKEN_KEY);
}

function persistSession({ token, user }) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  // A fresh login must land on its own role's home — drop any active-role
  // choice left over from a previous session so it can't bleed into (and
  // mis-route) the new one (e.g. a prior patient/doctor view).
  localStorage.removeItem('1ayurved_active_role');
}

/* ──────────────────────────── Patients ──────────────────────────── */

/**
 * @param {object} params
 * @param {string} [params.cursor]
 * @param {number} [params.limit]
 * @param {string} [params.search]   Free-text — matches name / UID / phone / email
 * @param {string} [params.clinic]   Clinic display name
 * @param {string} [params.gender]   F / M / Other
 * @param {string} [params.ageRange] 0-18 / 19-35 / 36-55 / 56-75 / 76+
 * @param {string} [params.status]   Active / New / Inactive / Deceased
 */
export async function fetchPatientsPage(params = {}) {
  if (USE_MOCK) return mock.fetchPatientsPage(params);
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/patients?${q.toString()}`);
}

export async function fetchPatientsSummary() {
  if (USE_MOCK) return mock.fetchPatientsSummary();
  return realGet('/api/v1/patients/summary');
}

export async function fetchPatientsFilterOptions() {
  if (USE_MOCK) return mock.fetchPatientsFilterOptions();
  return realGet('/api/v1/patients/filter-options');
}

/* ──────────────────────────── Notifications ──────────────────────────── */

/**
 * Fetch a page of notifications with optional filter.
 * @param {object} params
 * @param {string} [params.cursor]
 * @param {number} [params.limit]
 * @param {'all'|'unread'|'critical'} [params.scope]  default 'all'
 * @param {string} [params.kind]      compliance / data-correction / inventory / system
 * @param {string} [params.search]
 */
export async function fetchNotificationsPage(params = {}) {
  if (USE_MOCK) return mock.fetchNotificationsPage(params);
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/notifications?${q.toString()}`);
}

export async function fetchNotificationsSummary() {
  if (USE_MOCK) return mock.fetchNotificationsSummary();
  return realGet('/api/v1/notifications/summary');
}

export async function markNotificationRead(id) {
  if (USE_MOCK) return mock.markNotificationRead(id);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/notifications/${id}/read`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const err = new Error(`API ${res.status} for mark-read`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function markAllNotificationsRead() {
  if (USE_MOCK) return mock.markAllNotificationsRead();
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/notifications/read-all`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const err = new Error(`API ${res.status} for mark-all-read`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/* ──────────────────────────── Settings ──────────────────────────── */

/**
 * Fetch all Super Admin settings.
 * Bundle is tiny — fetched in one call, no pagination.
 */
export async function fetchSettings() {
  if (USE_MOCK) return mock.fetchSettings();
  return realGet('/api/v1/settings');
}

/**
 * Save one settings section.
 *
 * @param {string} section   'profile' | 'branding' | 'integrations' | 'system' | 'backup'
 * @param {object} values    full updated values object for that section
 */
export async function saveSettingsSection(section, values) {
  if (USE_MOCK) return mock.saveSettingsSection(section, values);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/settings/${section}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ values }),
  });
  if (!res.ok) {
    const err = new Error(`API ${res.status} for save settings/${section}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/* ──────────────────────────── Doctor · Dashboard ──────────────────────────── */

export async function fetchDoctorDashboard() {
  if (USE_MOCK) return mock.fetchDoctorDashboard();
  return realGet('/api/v1/doctor/dashboard');
}
/* ──────────────────────────── Doctor · Patients (panel) ──────────────────────────── */
/* No doctorId in the URL — the real backend infers the doctor from the JWT
   and returns only their panel. The mock reads the logged-in user instead. */

export async function fetchDoctorPatientsPage(params = {}) {
  if (USE_MOCK) return mock.fetchDoctorPatientsPage(params);
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/doctor/patients?${q.toString()}`);
}

export async function fetchDoctorPatientsSummary() {
  if (USE_MOCK) return mock.fetchDoctorPatientsSummary();
  return realGet('/api/v1/doctor/patients/summary');
}

export async function fetchDoctorPatientsFilterOptions() {
  if (USE_MOCK) return mock.fetchDoctorPatientsFilterOptions();
  return realGet('/api/v1/doctor/patients/filter-options');
}

export async function fetchDoctorPatientDetail(id) {
  if (USE_MOCK) return mock.fetchDoctorPatientDetail(id);
  return realGet(`/api/v1/doctor/patients/${encodeURIComponent(id)}`);
}

/* ──────────────────────────── Doctor · Calendar ──────────────────────────── */

/**
 * @param {object} params
 * @param {string} params.from  ISO date (inclusive) e.g. "2026-07-01"
 * @param {string} params.to    ISO date (inclusive)
 */
export async function fetchDoctorCalendar(params = {}) {
  if (USE_MOCK) return mock.fetchDoctorCalendar(params);
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/doctor/calendar?${q.toString()}`);
}
/* ──────────────────────────── Doctor · Schedule config ──────────────────────────── */

export async function fetchDoctorSchedule() {
  if (USE_MOCK) return mock.fetchDoctorSchedule();
  return realGet('/api/v1/doctor/schedule');
}

/**
 * @param {string} section  'workingHours' | 'slotConfig' | 'blockedDates' | 'vacations' | 'recurringHolidays'
 * @param {object|array} values
 */
export async function saveDoctorSchedule(section, values) {
  if (USE_MOCK) return mock.saveDoctorSchedule(section, values);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/schedule/${section}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ values }),
  });
  if (!res.ok) {
    const err = new Error(`API ${res.status} for save schedule/${section}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
/* ──────────────────────────── Doctor · Calendar mutations ──────────────────────────── */

export async function updateAppointmentStatus(apptId, status) {
  if (USE_MOCK) return mock.updateAppointmentStatus(apptId, status);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/appointments/${apptId}/status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for status`); e.status = res.status; throw e; }
  return res.json();
}

export async function rescheduleAppointment(apptId, target) {
  if (USE_MOCK) return mock.rescheduleAppointment(apptId, target);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/appointments/${apptId}/reschedule`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(target), // { date, start }
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for reschedule`); e.status = res.status; throw e; }
  return res.json();
}

export async function reserveEmergencySlot(slot) {
  if (USE_MOCK) return mock.reserveEmergencySlot(slot);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/appointments/emergency`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(slot), // { date, start, durationMin }
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for emergency slot`); e.status = res.status; throw e; }
  return res.json();
}
export async function updateAppointment(apptId, patch) {
  if (USE_MOCK) return mock.updateAppointment(apptId, patch);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/appointments/${apptId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(patch), // { reason?, durationMin? }
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for update appointment`); e.status = res.status; throw e; }
  return res.json();
}
/* ──────────────────────────── Doctor · Consultation ──────────────────────────── */

/* ──────────────────────────── Doctor · Consultation ──────────────────────────── */

/** Get or create the active draft for a patient (one active draft per patient). */
export async function openConsultation(patientId, patientNameHint, appointmentId) {
  if (USE_MOCK) return mock.openConsultation(patientId, patientNameHint);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/consultations/open`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json', Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ patientId, appointmentId }),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for open consultation`); e.status = res.status; throw e; }
  return res.json();
}

export async function openShortConsultation(patientId, patientNameHint) {
  if (USE_MOCK) return mock.openShortConsultation(patientId, patientNameHint);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/consultations/open-short`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ patientId }),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for open short consultation`); e.status = res.status; throw e; }
  return res.json();
}

export async function fetchConsultationRefData() {
  if (USE_MOCK) return mock.fetchConsultationRefData();
  return realGet('/api/v1/doctor/consultations/ref-data');
}

export async function fetchConsultation(recordId) {
  if (USE_MOCK) return mock.fetchConsultation(recordId);
  return realGet(`/api/v1/doctor/consultations/${encodeURIComponent(recordId)}`);
}

/** Previous completed consultation for this patient — "copy from last visit". */
export async function fetchPreviousConsultation(recordId) {
  if (USE_MOCK) return { found: false };
  return realGet(`/api/v1/doctor/consultations/${encodeURIComponent(recordId)}/previous`);
}

/** How many past visits prescribed each drug for this patient (byName/byCode). */
export async function fetchPatientDrugUsage(patientId) {
  if (USE_MOCK) return { byName: {}, byCode: {} };
  return realGet(`/api/v1/doctor/patients/${encodeURIComponent(patientId)}/drug-usage`);
}

/** Last 10 distinct complaint phrases from this patient's past consultations. */
export async function fetchPatientRecentComplaints(patientId) {
  if (USE_MOCK) return { complaints: [] };
  return realGet(`/api/v1/doctor/patients/${encodeURIComponent(patientId)}/recent-complaints`);
}

/* ──────────── Doctor-created templates (save as template) ──────────── */

export async function fetchConsultationTemplates(kind) {
  if (USE_MOCK) return { templates: [] };
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
  return realGet(`/api/v1/doctor/consultation-templates${q}`);
}

export async function createConsultationTemplate(payload) {
  if (USE_MOCK) return { id: Date.now(), ...payload };
  return realSend('/api/v1/doctor/consultation-templates', 'POST', payload);
}

export async function deleteConsultationTemplate(id) {
  if (USE_MOCK) return { deleted: true };
  return realSend(`/api/v1/doctor/consultation-templates/${encodeURIComponent(id)}`, 'DELETE');
}

export async function saveConsultationSection(recordId, sectionId, values) {
  if (USE_MOCK) return mock.saveConsultationSection(recordId, sectionId, values);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(
    `${API_BASE}/api/v1/doctor/consultations/${encodeURIComponent(recordId)}/sections/${sectionId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json', Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ values }),
    }
  );
  if (!res.ok) { const e = new Error(`API ${res.status} for save section`); e.status = res.status; throw e; }
  return res.json();
}

export async function completeConsultation(recordId) {
  if (USE_MOCK) return mock.completeConsultation(recordId);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(
    `${API_BASE}/api/v1/doctor/consultations/${encodeURIComponent(recordId)}/complete`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json', Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  if (!res.ok) { const e = new Error(`API ${res.status} for complete`); e.status = res.status; throw e; }
  return res.json();
}
export async function reopenConsultation(recordId) {
  if (USE_MOCK) return mock.reopenConsultation(recordId);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(
    `${API_BASE}/api/v1/doctor/consultations/${encodeURIComponent(recordId)}/reopen`,
    { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
  );
  if (!res.ok) { const e = new Error(`API ${res.status} for reopen`); e.status = res.status; throw e; }
  return res.json();
}
export async function addConsultationAddendum(recordId, note) {
  if (USE_MOCK) return mock.addConsultationAddendum(recordId, note);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(
    `${API_BASE}/api/v1/doctor/consultations/${encodeURIComponent(recordId)}/addendum`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ note }),
    }
  );
  if (!res.ok) {
    let msg = `API ${res.status} for addendum`;
    try { const j = await res.json(); if (j?.message) msg = j.message; } catch { /* ignore */ }
    const e = new Error(msg); e.status = res.status; throw e;
  }
  return res.json();
}
/* ──────────────────────────── Master Forms · Body maps (D11) ──────────────────────────── */

export async function saveBodyMaps(items) {
  if (USE_MOCK) return mock.saveBodyMaps(items);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/master-forms/body-maps`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for body maps`); e.status = res.status; throw e; }
  return res.json();
}
/* ──────────────────────────── Future endpoints ────────────────────────────
   Add new section fetchers here as each lands. Pattern is identical:
   USE_MOCK branch → mock fn, else realGet with query string.
   ───────────────────────────────────────────────────────────────────────── */
/* ──────────────────────────── Super Admin · Consultation records (D16) ──────────────────────────── */

export async function saListConsultations() {
  if (USE_MOCK) return mock.saListConsultations();
  return realGet('/api/v1/admin/consultations');
}

export async function saReopenConsultation(recordId) {
  if (USE_MOCK) return mock.saReopenConsultation(recordId);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(
    `${API_BASE}/api/v1/admin/consultations/${encodeURIComponent(recordId)}/reopen`,
    { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
  );
  if (!res.ok) { const e = new Error(`API ${res.status} for admin reopen`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Doctor · Reports ──────────────────────────── */

export async function fetchDoctorReports() {
  if (USE_MOCK) return mock.fetchDoctorReports();
  return realGet('/api/v1/doctor/reports');
}

/* ──────────────────────────── Staff · Front desk (Phase 7) ──────────────────────────── */

export async function fetchStaffToday() {
  if (USE_MOCK) return mock.fetchStaffToday();
  return realGet('/api/v1/staff/front-desk/today');
}

/* ──────────────────────────── Staff · Booking (Batch 32) ──────────────────────────── */

export async function fetchStaffBookingContext() {
  if (USE_MOCK) return mock.fetchStaffBookingContext();
  return realGet('/api/v1/staff/booking/context');
}

export async function fetchStaffSlots(params) {
  if (USE_MOCK) return mock.fetchStaffSlots(params);
  return realGet(`/api/v1/staff/booking/slots?doctorId=${encodeURIComponent(params.doctorId)}&date=${params.date}`);
}

// Patient-facing slot lookup — same engine, but under patient/* so a
// patient-scoped token can reach it (the staff route is blocked for patients).
export async function fetchPatientSlots(params) {
  if (USE_MOCK) return mock.fetchStaffSlots(params);
  return realGet(`/api/v1/patient/booking/slots?doctorId=${encodeURIComponent(params.doctorId)}&date=${params.date}`);
}

export async function createStaffAppointment(payload) {
  if (USE_MOCK) return mock.createStaffAppointment(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/appointments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for create appointment`); e.status = res.status; throw e; }
  return res.json();
}

export async function convertEmergencyHold(payload) {
  if (USE_MOCK) return mock.convertEmergencyHold(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/appointments/${encodeURIComponent(payload.apptId)}/convert`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for convert hold`); e.status = res.status; throw e; }
  return res.json();
}

export async function fetchStaffFollowups() {
  if (USE_MOCK) return mock.fetchStaffFollowups();
  return realGet('/api/v1/staff/followups');
}

/* ──────────────────────────── Staff · Billing (Batch 35) ──────────────────────────── */

export async function fetchBilling() {
  if (USE_MOCK) return mock.fetchBilling();
  return realGet('/api/v1/staff/billing');
}

export async function createInvoice(payload) {
  if (USE_MOCK) return mock.createInvoice(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/invoices`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for create invoice`); e.status = res.status; throw e; }
  return res.json();
}

export async function recordPayment(payload) {
  if (USE_MOCK) return mock.recordPayment(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/invoices/${encodeURIComponent(payload.invoiceId)}/payments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for payment`); e.status = res.status; throw e; }
  return res.json();
}

export async function issueRefund(payload) {
  if (USE_MOCK) return mock.issueRefund(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/invoices/${encodeURIComponent(payload.invoiceId)}/refunds`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for refund`); e.status = res.status; throw e; }
  return res.json();
}

export async function fetchStaffDay(date) {
  if (USE_MOCK) return mock.fetchStaffDay(date);
  return realGet(`/api/v1/staff/appointments?date=${date}`);
}

/* ──────────────────────────── Doctor · Education / Promotion / Help (B111) ──────────────────────────── */

export async function fetchDoctorEducation() {
  return realGet('/api/v1/doctor/education');
}

export async function fetchDoctorPromotion() {
  return realGet('/api/v1/doctor/promotion');
}

export async function fetchDoctorHelp() {
  return realGet('/api/v1/doctor/help');
}

/* ──────────────────────────── Promotions management (B113) ──────────────────────────── */

export async function fetchPromotions(clinicId) {
  const q = clinicId ? `?clinicId=${encodeURIComponent(clinicId)}` : '';
  return realGet(`/api/v1/staff/promotions${q}`);
}

export async function savePromotion(payload) {
  const { id, ...body } = payload;
  return id
    ? realSend(`/api/v1/staff/promotions/${encodeURIComponent(id)}`, 'PUT', body)
    : realSend('/api/v1/staff/promotions', 'POST', body);
}

export async function togglePromotion(id) {
  return realSend(`/api/v1/staff/promotions/${encodeURIComponent(id)}/toggle`, 'POST');
}

export async function deletePromotion(id) {
  return realSend(`/api/v1/staff/promotions/${encodeURIComponent(id)}`, 'DELETE');
}

/* ──────────────────────────── Clinic drug master (B115) ──────────────────────────── */

export async function fetchClinicDrugs(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/staff/clinic-drugs?${q.toString()}`);
}

export async function saveClinicDrug(payload) {
  const { id, clinicId, ...body } = payload;
  const withClinic = clinicId ? { ...body, clinicId } : body;
  return id
    ? realSend(`/api/v1/staff/clinic-drugs/${encodeURIComponent(id)}${clinicId ? `?clinicId=${encodeURIComponent(clinicId)}` : ''}`, 'PUT', withClinic)
    : realSend('/api/v1/staff/clinic-drugs', 'POST', withClinic);
}

export async function deleteClinicDrug(id, clinicId) {
  const qs = clinicId ? `?clinicId=${encodeURIComponent(clinicId)}` : '';
  return realSend(`/api/v1/staff/clinic-drugs/${encodeURIComponent(id)}${qs}`, 'DELETE');
}

/* ──────────────────────────── Clinic procedure master (B116) ──────────────────────────── */

export async function fetchClinicProcedures(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  return realGet(`/api/v1/staff/clinic-procedures?${q.toString()}`);
}

export async function saveClinicProcedure(payload) {
  const { id, clinicId, ...body } = payload;
  const withClinic = clinicId ? { ...body, clinicId } : body;
  return id
    ? realSend(`/api/v1/staff/clinic-procedures/${encodeURIComponent(id)}${clinicId ? `?clinicId=${encodeURIComponent(clinicId)}` : ''}`, 'PUT', withClinic)
    : realSend('/api/v1/staff/clinic-procedures', 'POST', withClinic);
}

export async function deleteClinicProcedure(id, clinicId) {
  const qs = clinicId ? `?clinicId=${encodeURIComponent(clinicId)}` : '';
  return realSend(`/api/v1/staff/clinic-procedures/${encodeURIComponent(id)}${qs}`, 'DELETE');
}

/* Per-clinic price + GST override for a NETWORK procedure (B116). The network
   name/category/duration stay Super-Admin-governed; only the money is clinic-set. */
export async function setClinicProcedurePrice({ id, clinicId, manualMrp, gstPercent }) {
  const body = { manualMrp, gstPercent, ...(clinicId ? { clinicId } : {}) };
  return realSend(`/api/v1/staff/clinic-procedures/${encodeURIComponent(id)}/price${clinicId ? `?clinicId=${encodeURIComponent(clinicId)}` : ''}`, 'PUT', body);
}

export async function clearClinicProcedurePrice(id, clinicId) {
  const qs = clinicId ? `?clinicId=${encodeURIComponent(clinicId)}` : '';
  return realSend(`/api/v1/staff/clinic-procedures/${encodeURIComponent(id)}/price${qs}`, 'DELETE');
}

/* Effective (network + this clinic) formularies — what doctors prescribe from
   and Reception bills from (B117). Auto-scoped to the caller's clinic. */
export async function fetchEffectiveDrugs() {
  return realGet('/api/v1/staff/clinic-drugs/effective');
}
export async function fetchEffectiveProcedures() {
  return realGet('/api/v1/staff/clinic-procedures/effective');
}

/* ──────────────────────────── Clinic master-form overlay (B118) ──────────────────────────── */

export async function fetchClinicMasterFormLists(clinicId) {
  const q = clinicId ? `?clinicId=${encodeURIComponent(clinicId)}` : '';
  return realGet(`/api/v1/staff/clinic-master-forms${q}`);
}

export async function fetchClinicMasterFormEntries(path, clinicId) {
  const q = new URLSearchParams({ path });
  if (clinicId) q.set('clinicId', clinicId);
  return realGet(`/api/v1/staff/clinic-master-forms/entries?${q.toString()}`);
}

export async function saveClinicMasterFormEntry({ id, path, clinicId, ...body }) {
  const q = new URLSearchParams({ path });
  if (clinicId) q.set('clinicId', clinicId);
  return id
    ? realSend(`/api/v1/staff/clinic-master-forms/entries/${encodeURIComponent(id)}?${q.toString()}`, 'PUT', body)
    : realSend(`/api/v1/staff/clinic-master-forms/entries?${q.toString()}`, 'POST', body);
}

export async function deleteClinicMasterFormEntry(id, path, clinicId) {
  const q = new URLSearchParams({ path });
  if (clinicId) q.set('clinicId', clinicId);
  return realSend(`/api/v1/staff/clinic-master-forms/entries/${encodeURIComponent(id)}?${q.toString()}`, 'DELETE');
}

/* ──────────────────────────── Panchakarma plans (B125) ──────────────────────────── */

export async function fetchPkPlans(clinicId) {
  const q = clinicId ? `?clinicId=${encodeURIComponent(clinicId)}` : '';
  return realGet(`/api/v1/staff/pk-plans${q}`);
}
export async function savePkPlan(payload) {
  const { id, ...body } = payload;
  return id
    ? realSend(`/api/v1/staff/pk-plans/${encodeURIComponent(id)}`, 'PUT', body)
    : realSend('/api/v1/staff/pk-plans', 'POST', body);
}
export async function deletePkPlan(id) {
  return realSend(`/api/v1/staff/pk-plans/${encodeURIComponent(id)}`, 'DELETE');
}

/* ──────────────────────────── Staff · Coupons (B107) ──────────────────────────── */

async function realSend(path, method, body) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    let msg = `API ${res.status}`;
    try { const j = await res.json(); msg = j.message || msg; } catch { /* non-JSON */ }
    const e = new Error(msg); e.status = res.status; throw e;
  }
  return res.json();
}

export async function fetchCoupons() {
  return realGet('/api/v1/staff/coupons');
}

/** Live preview — returns { code, description, discount } or throws with the reason. */
export async function validateCoupon(payload) {
  return realSend('/api/v1/staff/coupons/validate', 'POST', payload);
}

/** Create when no id, update when id present. */
export async function saveCoupon(payload) {
  const { id, ...body } = payload;
  return id
    ? realSend(`/api/v1/staff/coupons/${encodeURIComponent(id)}`, 'PUT', body)
    : realSend('/api/v1/staff/coupons', 'POST', body);
}

export async function toggleCoupon(id) {
  return realSend(`/api/v1/staff/coupons/${encodeURIComponent(id)}/toggle`, 'POST');
}

export async function deleteCoupon(id) {
  return realSend(`/api/v1/staff/coupons/${encodeURIComponent(id)}`, 'DELETE');
}

/* ──────────────────────────── Staff · Merge patients (Batch 37) ──────────────────────────── */

export async function submitMergeRequest(payload) {
  if (USE_MOCK) return mock.submitMergeRequest(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/merge-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for merge request`); e.status = res.status; throw e; }
  return res.json();
}

export async function fetchMyMergeRequests() {
  if (USE_MOCK) return mock.fetchMyMergeRequests();
  return realGet('/api/v1/staff/merge-requests');
}

/* ──────────────────────────── Staff · Complaints (Batch 38) ──────────────────────────── */

export async function fetchComplaints() {
  if (USE_MOCK) return mock.fetchComplaints();
  return realGet('/api/v1/staff/complaints');
}

export async function createComplaint(payload) {
  if (USE_MOCK) return mock.createComplaint(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/complaints`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let msg = `API ${res.status} for create complaint`;
    try { const j = await res.json(); if (j && j.message) msg = j.message; } catch { /* non-JSON */ }
    const e = new Error(msg); e.status = res.status; throw e;
  }
  return res.json();
}

export async function updateComplaint(payload) {
  if (USE_MOCK) return mock.updateComplaint(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/complaints/${encodeURIComponent(payload.id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload.patch),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for update complaint`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Staff · Masseur console (Batch 39) ──────────────────────────── */

export async function fetchMasseurDay(date) {
  if (USE_MOCK) return mock.fetchMasseurDay(date);
  return realGet(`/api/v1/staff/masseur/sessions?date=${date}`);
}

export async function updatePkSession(payload) {
  if (USE_MOCK) return mock.updatePkSession(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/masseur/sessions/${encodeURIComponent(payload.apptId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for PK session`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Doctor · PK Consultant Sheet (Batch 40) ──────────────────────────── */

export async function fetchPkTemplates() {
  if (USE_MOCK) return mock.fetchPkTemplates();
  return realGet('/api/v1/doctor/pk-templates');
}

export async function createPkCourse(payload) {
  if (USE_MOCK) return mock.createPkCourse(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/pk-courses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for create PK course`); e.status = res.status; throw e; }
  return res.json();
}

export async function fetchPkCourses(params) {
  if (USE_MOCK) return mock.fetchPkCourses(params);
  return realGet(`/api/v1/doctor/pk-courses?doctor=${encodeURIComponent(params?.doctorFilter || 'all')}`);
}

export async function fetchPkCourse(courseId) {
  if (USE_MOCK) return mock.fetchPkCourse(courseId);
  return realGet(`/api/v1/doctor/pk-courses/${encodeURIComponent(courseId)}`);
}

export async function updatePkCourse(payload) {
  if (USE_MOCK) return mock.updatePkCourse(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/pk-courses/${encodeURIComponent(payload.courseId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload.patch),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for PK course`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Doctor · Accounts & Inventory (Batch 41) ──────────────────────────── */

export async function fetchDoctorAccounts(params) {
  if (USE_MOCK) return mock.fetchDoctorAccounts(params);
  return realGet(`/api/v1/doctor/accounts?period=${params?.period || 'month'}`);
}

export async function adjustInventory(payload) {
  if (USE_MOCK) return mock.adjustInventory(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/inventory/${encodeURIComponent(payload.sku)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for inventory`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Doctor · Settings (Batch 42) ──────────────────────────── */

export async function fetchDoctorSettings() {
  if (USE_MOCK) return mock.fetchDoctorSettings();
  return realGet('/api/v1/doctor/settings');
}

export async function saveDoctorSettings(prefs) {
  if (USE_MOCK) return mock.saveDoctorSettings(prefs);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(prefs),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for settings`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Onboarding documents (authed self-service) ──────────────────────────── */

export async function fetchDoctorOnboardingStatus() {
  return realGet('/api/v1/doctor/onboarding-status');
}
export async function submitDoctorKyc(payload) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/kyc/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for kyc submit`); e.status = res.status; try { e.message = (await res.json())?.message || e.message; } catch { /* keep default */ } throw e; }
  return res.json();
}
export async function fetchClinicOnboardingStatus() {
  return realGet('/api/v1/clinic/onboarding-status');
}
export async function submitClinicKyc(payload) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/clinic/kyc/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for kyc submit`); e.status = res.status; try { e.message = (await res.json())?.message || e.message; } catch { /* keep default */ } throw e; }
  return res.json();
}

/* Public onboarding — details form schema + section save (tokenised link). */
export async function fetchOnboardSchema(token) {
  return realGetPublic(`/api/v1/public/onboard/${encodeURIComponent(token)}/schema`);
}
export async function saveOnboardSection(token, body) {
  return realSendPublic(`/api/v1/public/onboard/${encodeURIComponent(token)}/section`, 'POST', body);
}

/* Super Admin — approve a whole KYC subject. */
export async function approveKycSubject(payload) {
  // payload: { ownerType, ownerId }
  return realSend('/api/v1/kyc/approve', 'POST', payload);
}

/* Public self-registration (no invite): clinic search + phone OTP + submit. */
export async function fetchRegisterClinics(q) {
  const qs = new URLSearchParams();
  if (q) qs.set('q', q);
  return realGetPublic(`/api/v1/public/register/clinics?${qs.toString()}`);
}
export async function sendRegisterOtp(body) {
  return realSendPublic('/api/v1/public/register/otp', 'POST', body);
}
export async function registerDoctor(body) {
  return realSendPublic('/api/v1/public/register/doctor', 'POST', body);
}
export async function registerClinic(body) {
  return realSendPublic('/api/v1/public/register/clinic', 'POST', body);
}

/* ──────────────────────────── Staff · Pharmacy queue (Batch 43) ──────────────────────────── */

export async function fetchPharmacyQueue() {
  if (USE_MOCK) return mock.fetchPharmacyQueue();
  return realGet('/api/v1/staff/pharmacy/queue');
}

export async function dispenseOrder(payload) {
  if (USE_MOCK) return mock.dispenseOrder(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/pharmacy/${encodeURIComponent(payload.orderId)}/dispense`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for dispense`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Staff · PK consumables desk (Assistant, T3) ──────────────────────────── */

export async function fetchPkConsumables() {
  return realGet('/api/v1/staff/pk-consumables');
}

export async function addPkConsumable(payload) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/pk-consumables`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for add consumable`); e.status = res.status; throw e; }
  return res.json();
}

export async function adjustPkConsumable(payload) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/pk-consumables/${encodeURIComponent(payload.sku)}/stock`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for adjust consumable`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Staff · RMO pre-PK clearance (Batch 44) ──────────────────────────── */

export async function fetchRmoQueue() {
  if (USE_MOCK) return mock.fetchRmoQueue();
  return realGet('/api/v1/staff/rmo/queue');
}

export async function submitRmoClearance(payload) {
  if (USE_MOCK) return mock.submitRmoClearance(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/rmo/${encodeURIComponent(payload.courseId)}/clearance`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for clearance`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Doctor · Accounts complete (Batch 45) ──────────────────────────── */

export async function fetchAccountsExtras() {
  if (USE_MOCK) return mock.fetchAccountsExtras();
  return realGet('/api/v1/doctor/accounts/extras');
}

function acctPost(path, payload) {
  const token = localStorage.getItem('1ayurved_token');
  return fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  }).then((res) => {
    if (!res.ok) { const e = new Error(`API ${res.status}`); e.status = res.status; throw e; }
    return res.json();
  });
}

export async function addExpenditure(p) { if (USE_MOCK) return mock.addExpenditure(p); return acctPost('/api/v1/doctor/expenditures', p); }
export async function markExpenditurePaid(p) { if (USE_MOCK) return mock.markExpenditurePaid(p); return acctPost(`/api/v1/doctor/expenditures/${encodeURIComponent(p.id)}/pay`, p); }
export async function createPurchaseOrder(p) { if (USE_MOCK) return mock.createPurchaseOrder(p); return acctPost('/api/v1/doctor/purchase-orders', p); }
export async function receivePurchaseOrder(p) { if (USE_MOCK) return mock.receivePurchaseOrder(p); return acctPost(`/api/v1/doctor/purchase-orders/${encodeURIComponent(p.orderId)}/receive`, p); }
export async function toggleCompliance(p) { if (USE_MOCK) return mock.toggleCompliance(p); return acctPost(`/api/v1/doctor/compliance/${encodeURIComponent(p.id)}/toggle`, p); }
export async function fetchCosting() { if (USE_MOCK) return mock.fetchCosting(); return realGet('/api/v1/doctor/costing'); }
export async function saveCosting(p) { if (USE_MOCK) return mock.saveCosting(p); return acctPost('/api/v1/doctor/costing', p); }

/* ──────────────────────────── Doctor · Settings clinic view (Batch 45) ──────────────────────────── */

export async function fetchMyClinicConfig() {
  if (USE_MOCK) return mock.fetchMyClinicConfig();
  return realGet('/api/v1/doctor/clinic-config');
}

/* ──────────────────────────── Staff · Incident log (Batch 46) ──────────────────────────── */

export async function fetchIncidents(params) {
  if (USE_MOCK) return mock.fetchIncidents(params);
  return realGet(`/api/v1/staff/incidents?window=${params?.window || 'year'}`);
}
export async function createIncident(payload) {
  if (USE_MOCK) return mock.createIncident(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/incidents`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for incident`); e.status = res.status; throw e; }
  return res.json();
}
export async function resolveIncident(payload) {
  if (USE_MOCK) return mock.resolveIncident(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/incidents/${encodeURIComponent(payload.id)}/resolve`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for resolve`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Staff · RMO monitor + Masseur history (Batch 47) ──────────────────────────── */

export async function fetchRmoMonitor() {
  if (USE_MOCK) return mock.fetchRmoMonitor();
  return realGet('/api/v1/staff/rmo/monitor');
}
export async function fetchMasseurHistory(params) {
  if (USE_MOCK) return mock.fetchMasseurHistory(params);
  return realGet(`/api/v1/staff/masseur/history?days=${params?.days || 14}`);
}

/* ──────────────────────────── Patient portal (Batch 48) ──────────────────────────── */

export async function fetchPatientHome() {
  if (USE_MOCK) return mock.fetchPatientHome();
  return realGet('/api/v1/patient/home');
}
export async function fetchPatientAppointments() {
  if (USE_MOCK) return mock.fetchPatientAppointments();
  return realGet('/api/v1/patient/appointments');
}
export async function fetchPatientBookingContext() {
  if (USE_MOCK) return mock.fetchPatientBookingContext();
  return realGet('/api/v1/patient/booking-context');
}
export async function createPatientAppointment(payload) {
  if (USE_MOCK) return mock.createPatientAppointment(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/patient/appointments`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for booking`); e.status = res.status; throw e; }
  return res.json();
}
export async function cancelPatientAppointment(payload) {
  if (USE_MOCK) return mock.cancelPatientAppointment(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/patient/appointments/${encodeURIComponent(payload.apptId)}/cancel`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for cancel`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Patient · Records + PK journey (Batch 49) ──────────────────────────── */

export async function fetchPatientRecords() {
  if (USE_MOCK) return mock.fetchPatientRecords();
  return realGet('/api/v1/patient/records');
}
export async function fetchPatientRecord(recordId) {
  if (USE_MOCK) return mock.fetchPatientRecord(recordId);
  return realGet(`/api/v1/patient/records/${encodeURIComponent(recordId)}`);
}
export async function fetchPatientPkJourney() {
  if (USE_MOCK) return mock.fetchPatientPkJourney();
  return realGet('/api/v1/patient/pk-journey');
}
export async function fetchPatientLabReports() {
  return realGet('/api/v1/patient/lab-reports');
}
export async function fetchPatientImages() {
  return realGet('/api/v1/patient/images');
}
export async function fetchPatientUploads() {
  return realGet('/api/v1/patient/uploads');
}
export async function uploadPatientFile(payload) {
  return realSend('/api/v1/patient/uploads', 'POST', payload);
}
export async function deletePatientUpload(id) {
  return realSend(`/api/v1/patient/uploads/${encodeURIComponent(id)}`, 'DELETE');
}

export async function uploadClinicLogo(payload) {
  return realSend('/api/v1/clinics/logo-upload', 'POST', payload);
}
export async function fetchClinicDaySummary({ clinicId, date } = {}) {  const qs = new URLSearchParams();
  if (clinicId) qs.set('clinicId', clinicId);
  if (date) qs.set('date', date);
  const q = qs.toString();
  return realGet(`/api/v1/clinic/day-summary${q ? `?${q}` : ''}`);
}

/* ── Clinic-side authoring: patient lab reports + uploads (B141) ── */
export async function fetchClinicPatientLabs(patientId) {
  return realGet(`/api/v1/clinic/patients/${encodeURIComponent(patientId)}/lab-reports`);
}
export async function createClinicPatientLab({ patientId, ...body }) {
  return realSend(`/api/v1/clinic/patients/${encodeURIComponent(patientId)}/lab-reports`, 'POST', body);
}
export async function deleteClinicPatientLab({ patientId, id }) {
  return realSend(`/api/v1/clinic/patients/${encodeURIComponent(patientId)}/lab-reports/${encodeURIComponent(id)}`, 'DELETE');
}
export async function fetchClinicPatientUploads(patientId) {
  return realGet(`/api/v1/clinic/patients/${encodeURIComponent(patientId)}/uploads`);
}
export async function uploadClinicPatientFile({ patientId, ...body }) {
  return realSend(`/api/v1/clinic/patients/${encodeURIComponent(patientId)}/uploads`, 'POST', body);
}
export async function deleteClinicPatientUpload({ patientId, id }) {
  return realSend(`/api/v1/clinic/patients/${encodeURIComponent(patientId)}/uploads/${encodeURIComponent(id)}`, 'DELETE');
}

/* ──────────────────────────── Patient · Bills + profile (Batch 50) ──────────────────────────── */

export async function fetchPatientBills() {
  if (USE_MOCK) return mock.fetchPatientBills();
  return realGet('/api/v1/patient/bills');
}
export async function fetchPatientBill(invoiceId) {
  if (USE_MOCK) return mock.fetchPatientBill(invoiceId);
  return realGet(`/api/v1/patient/bills/${encodeURIComponent(invoiceId)}`);
}
export async function initiatePatientPayment(payload) {
  if (USE_MOCK) return mock.initiatePatientPayment(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/patient/payments`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for payment`); e.status = res.status; throw e; }
  return res.json();
}
export async function fetchPatientProfile() {
  if (USE_MOCK) return mock.fetchPatientProfile();
  return realGet('/api/v1/patient/profile');
}
export async function updateNotificationPrefs(prefs) {
  return realSend('/api/v1/patient/notification-prefs', 'PATCH', prefs);
}
export async function requestProfileUpdate(payload) {
  if (USE_MOCK) return mock.requestProfileUpdate(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/patient/profile/update-request`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for update request`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Patient · Consent · Complaint · Feedback (Batch 51) ──────────────────────────── */

export async function fetchPatientConsent() {
  if (USE_MOCK) return mock.fetchPatientConsent();
  return realGet('/api/v1/patient/consent');
}
export async function signPatientConsent(payload) {
  if (USE_MOCK) return mock.signPatientConsent(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/patient/consent`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for consent`); e.status = res.status; throw e; }
  return res.json();
}
export async function filePatientComplaint(payload) {
  if (USE_MOCK) return mock.filePatientComplaint(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/patient/complaints`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for complaint`); e.status = res.status; throw e; }
  return res.json();
}
export async function fetchMyComplaints() {
  if (USE_MOCK) return mock.fetchMyComplaints();
  return realGet('/api/v1/patient/complaints');
}
export async function fetchFeedbackTargets() {
  if (USE_MOCK) return mock.fetchFeedbackTargets();
  return realGet('/api/v1/patient/feedback/visits');
}
export async function submitPatientFeedback(payload) {
  if (USE_MOCK) return mock.submitPatientFeedback(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/patient/feedback`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for feedback`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Staff · Quick add patient (Batch 52) ──────────────────────────── */

export async function quickAddPatient(payload) {
  if (USE_MOCK) return mock.quickAddPatient(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/staff/patients/quick`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for quick add`); e.status = res.status; throw e; }
  return res.json();
}

export async function fetchMasseurCalendar(date) {
  if (USE_MOCK) return mock.fetchMasseurCalendar(date);
  return realGet(`/api/v1/staff/masseur/calendar?date=${date}`);
}

/* ──────────────────────────── Clinic Panel (Batch 54) ──────────────────────────── */

export async function fetchClinicPanel(clinicId) {
  if (USE_MOCK) return mock.fetchClinicPanel(clinicId);
  return realGet(`/api/v1/clinics/${encodeURIComponent(clinicId)}/panel`);
}
export async function fetchClinicPatientsSearch(clinicId, { search = '', limit = 50 } = {}) {
  if (USE_MOCK) {
    const panel = await mock.fetchClinicPanel(clinicId);
    const all = panel?.patients || [];
    const t = search.trim().toLowerCase();
    const matches = t ? all.filter((p) => `${p.name} ${p.id} ${p.phone}`.toLowerCase().includes(t)) : all;
    return { patients: matches.slice(0, limit), total: matches.length };
  }
  const qs = new URLSearchParams();
  if (search) qs.set('search', search);
  if (limit) qs.set('limit', String(limit));
  return realGet(`/api/v1/clinics/${encodeURIComponent(clinicId)}/patients?${qs.toString()}`);
}
export async function fetchMyClinics() {
  if (USE_MOCK) return mock.fetchMyClinics();
  return realGet('/api/v1/my/clinics');
}
export async function createClinicStaff(payload) {
  if (USE_MOCK) return mock.createClinicStaff(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/clinics/${encodeURIComponent(payload.clinicId)}/staff`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for clinic staff`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── D21 · Per-clinic GST settings (Batch 56) ──────────────────────────── */

export async function fetchClinicBillingSettings(clinicId) {
  if (USE_MOCK) return mock.fetchClinicBillingSettings(clinicId);
  return realGet(`/api/v1/clinics/${encodeURIComponent(clinicId || 'mine')}/billing-settings`);
}
export async function saveClinicBillingSettings(payload) {
  if (USE_MOCK) return mock.saveClinicBillingSettings(payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/clinics/${encodeURIComponent(payload.clinicId)}/billing-settings`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for billing settings`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── D22 · Clinic-detail editing (Batch 57) ──────────────────────────── */

export async function updateClinicDetails(payload) {
  if (USE_MOCK) return mock.updateClinicDetails(payload);
  const token = localStorage.getItem('1ayurved_token');
  // Backend reads allowed fields off the request root (only() over an
  // allow-list) — send them flat, not wrapped in `patch`.
  const { clinicId, patch, ...rest } = payload || {};
  const body = { ...(patch || {}), ...rest };
  const res = await fetch(`${API_BASE}/api/v1/clinics/${encodeURIComponent(clinicId)}/details`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok) { const e = new Error(`API ${res.status} for clinic details`); e.status = res.status; throw e; }
  return res.json();
}

// Super Admin cross-clinic patient record (read-only aggregate).
export async function fetchAdminPatientDetail(id) {
  return realGet(`/api/v1/patients/${encodeURIComponent(id)}/detail`);
}

/* ──────────────────────────── Communication Hub (B58) ──────────────────────────── */

export async function fetchCommOverview() {
  if (USE_MOCK && mock.fetchCommOverview) return mock.fetchCommOverview();
  return realGet('/api/v1/comm/overview');
}

export async function fetchCommMessages(params = {}) {
  if (USE_MOCK && mock.fetchCommMessages) return mock.fetchCommMessages(params);
  const q = new URLSearchParams();
  if (params.box) q.set('box', params.box);
  if (params.channel) q.set('channel', params.channel);
  if (params.q) q.set('q', params.q);
  return realGet(`/api/v1/comm/messages?${q.toString()}`);
}

export async function fetchCommThread(id) {
  if (USE_MOCK && mock.fetchCommThread) return mock.fetchCommThread(id);
  return realGet(`/api/v1/comm/messages/${encodeURIComponent(id)}`);
}

async function commWrite(path, method, body) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    // Surface the server's reason (e.g. "This number isn't linked to any
    // registered patient.") instead of a bare status code.
    let msg = `API ${res.status} for ${path}`;
    try { const j = await res.json(); if (j && j.message) msg = j.message; } catch { /* non-JSON */ }
    const e = new Error(msg); e.status = res.status; throw e;
  }
  return res.json();
}

export async function sendCommMessage(payload) {
  if (USE_MOCK && mock.sendCommMessage) return mock.sendCommMessage(payload);
  return commWrite('/api/v1/comm/messages', 'POST', payload);
}

export async function replyCommMessage({ id, body }) {
  if (USE_MOCK && mock.replyCommMessage) return mock.replyCommMessage({ id, body });
  return commWrite(`/api/v1/comm/messages/${encodeURIComponent(id)}/reply`, 'POST', { body });
}

export async function markCommRead(id) {
  if (USE_MOCK && mock.markCommRead) return mock.markCommRead(id);
  return commWrite(`/api/v1/comm/messages/${encodeURIComponent(id)}/read`, 'PATCH');
}

export async function fetchCommTemplates() {
  if (USE_MOCK && mock.fetchCommTemplates) return mock.fetchCommTemplates();
  return realGet('/api/v1/comm/templates');
}

export async function fetchCommDirectory() {
  if (USE_MOCK && mock.fetchCommDirectory) return mock.fetchCommDirectory();
  return realGet('/api/v1/comm/directory');
}

export async function fetchCommCampaigns() {
  if (USE_MOCK && mock.fetchCommCampaigns) return mock.fetchCommCampaigns();
  return realGet('/api/v1/comm/campaigns');
}

export async function createCommCampaign(payload) {
  if (USE_MOCK && mock.createCommCampaign) return mock.createCommCampaign(payload);
  return commWrite('/api/v1/comm/campaigns', 'POST', payload);
}

export async function fetchCommCertificates() {
  if (USE_MOCK && mock.fetchCommCertificates) return mock.fetchCommCertificates();
  return realGet('/api/v1/comm/certificates');
}

export async function issueCommCertificate(payload) {
  if (USE_MOCK && mock.issueCommCertificate) return mock.issueCommCertificate(payload);
  return commWrite('/api/v1/comm/certificates', 'POST', payload);
}

/* ──────────────────────────── Public booking (B59 · no auth) ──────────────────────────── */

async function realGetPublic(path) {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) { const e = new Error(`API ${res.status} for ${path}`); e.status = res.status; throw e; }
  return res.json();
}

export async function fetchPublicClinics({ lat, lng } = {}) {
  const q = new URLSearchParams();
  if (lat != null && lng != null) { q.set('lat', lat); q.set('lng', lng); }
  return realGetPublic(`/api/v1/public/clinics?${q.toString()}`);
}

export async function fetchPublicSlots({ doctorId, date }) {
  const q = new URLSearchParams({ doctorId, date });
  return realGetPublic(`/api/v1/public/slots?${q.toString()}`);
}

export async function fetchReturningPatient(phone) {
  return realGetPublic(`/api/v1/public/returning?phone=${encodeURIComponent(phone)}`);
}

export async function publicBook(payload) {
  const res = await fetch(`${API_BASE}/api/v1/public/appointments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok && res.status !== 409) { const e = new Error(`API ${res.status}`); e.status = res.status; e.data = data; throw e; }
  return { status: res.status, ...data };
}

/* ──────────────────────────── Onboarding invites + KYC ──────────────────────────── */

/* Public (no-auth) writes — the tokenised document-submission link. */
async function realSendPublic(path, method, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    let msg = `API ${res.status}`;
    try { const j = await res.json(); msg = j.message || msg; } catch { /* non-JSON */ }
    const e = new Error(msg); e.status = res.status; throw e;
  }
  return res.json();
}

export async function fetchPublicOnboard(token) {
  return realGetPublic(`/api/v1/public/onboard/${encodeURIComponent(token)}`);
}
export async function sendOnboardOtp(token) {
  return realSendPublic(`/api/v1/public/onboard/${encodeURIComponent(token)}/otp`, 'POST');
}
export async function verifyOnboardOtp(token, body) {
  return realSendPublic(`/api/v1/public/onboard/${encodeURIComponent(token)}/verify`, 'POST', body);
}
export async function submitOnboardDoc(token, body) {
  return realSendPublic(`/api/v1/public/onboard/${encodeURIComponent(token)}/submit`, 'POST', body);
}

/* Authed — SA / Clinic invite management + KYC review. */
export async function fetchInvites(kind) {
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
  return realGet(`/api/v1/invites${q}`);
}
export async function createInvite(body) {
  return realSend('/api/v1/invites', 'POST', body);
}
export async function resendInvite(id) {
  return realSend(`/api/v1/invites/${encodeURIComponent(id)}/resend`, 'POST');
}
export async function revokeInvite(id) {
  return realSend(`/api/v1/invites/${encodeURIComponent(id)}`, 'DELETE');
}
export async function fetchKycSubjects() {
  return realGet('/api/v1/kyc');
}
export async function fetchKycSubject({ ownerType, ownerId }) {
  const q = new URLSearchParams({ ownerType, ownerId });
  return realGet(`/api/v1/kyc/subject?${q.toString()}`);
}
export async function verifyKycDoc(payload) {
  // payload: { ownerType, ownerId, key }
  return realSend('/api/v1/kyc/verify', 'POST', payload);
}
export async function rejectKycDoc(payload) {
  // payload: { ownerType, ownerId, key, reason }
  return realSend('/api/v1/kyc/reject', 'POST', payload);
}

/* Fetch a private uploaded document (with bearer) and return an object URL to open. */
export async function openUploadedDoc(path) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/uploads/document?path=${encodeURIComponent(path)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) { const e = new Error(`API ${res.status}`); e.status = res.status; throw e; }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/* ──────────────────────────── PK day-log (B62) ──────────────────────────── */
export async function logPkDay(courseId, payload) {
  if (USE_MOCK && mock.logPkDay) return mock.logPkDay(courseId, payload);
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/doctor/pk-courses/${encodeURIComponent(courseId)}/day-log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const e = new Error(`API ${res.status}`); e.status = res.status; throw e; }
  return res.json();
}

/* ──────────────────────────── Public payments · Razorpay (B65) ──────────────────────────── */
export async function createPaymentOrder(payload) {
  const res = await fetch(`${API_BASE}/api/v1/public/payments/order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  return { status: res.status, ...data };
}

export async function verifyPayment(payload) {
  const res = await fetch(`${API_BASE}/api/v1/public/payments/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  return { status: res.status, ...data };
}

/* ────────────────── Online invoice payments · Razorpay (authenticated) ──────────────────
   The invoice-based sibling of the public booking payment above. The server
   reads the amount from the invoice's own due — the client never sends it. All
   four helpers return { status, ...data } so the caller can branch on 503
   (gateway off → pay at desk), 409 (already settled → refund) and 400 (bad
   signature) the same way the public flow does. */

// Load Razorpay Checkout once; resolves true when window.Razorpay is available.
export function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

async function authPost(path, payload) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: payload ? JSON.stringify(payload) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  return { status: res.status, ...data };
}

export function staffInvoiceOnlineOrder(invoiceId) {
  return authPost(`/api/v1/staff/invoices/${encodeURIComponent(invoiceId)}/online/order`);
}
export function staffInvoiceOnlineVerify(invoiceId, payload) {
  return authPost(`/api/v1/staff/invoices/${encodeURIComponent(invoiceId)}/online/verify`, payload);
}
export function patientBillOnlineOrder(invoiceId) {
  return authPost(`/api/v1/patient/bills/${encodeURIComponent(invoiceId)}/online/order`);
}
export function patientBillOnlineVerify(invoiceId, payload) {
  return authPost(`/api/v1/patient/bills/${encodeURIComponent(invoiceId)}/online/verify`, payload);
}

/* ────────────────── Master Forms · version history + rollback ────────────────── */
export async function fetchMasterFormVersions() {
  return realGet('/api/v1/master-forms/versions');
}
export async function restoreMasterFormVersion(id) {
  const res = await authPost(`/api/v1/master-forms/versions/${encodeURIComponent(id)}/restore`);
  if (res.status >= 400) throw new Error(res.message || 'Restore failed');
  return res;
}

/* ────────────────── Drugs bulk import (round-trip CSV) ────────────────── */
export async function importDrugs(rows) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/drugs/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ rows }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Import failed (${res.status})`);
  return data;
}

/* ────────────────── Patient by id + communication consent ────────────────── */
export async function fetchPatient(id) {
  return realGet(`/api/v1/patients/${encodeURIComponent(id)}`);
}
export async function setCommConsent(id, flags) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/patients/${encodeURIComponent(id)}/comm-consent`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(flags),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Update failed (${res.status})`);
  return data;
}

/* ── Patient invoice online payment: Razorpay verify ── */
export async function verifyPatientPayment(payload) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/patient/payments/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.message || `Payment verify failed (${res.status})`); e.status = res.status; throw e; }
  return data;
}

/* ── PK digital consent (reception) ── */
export async function fetchPkConsent(courseId) {
  return realGet(`/api/v1/pk-courses/${encodeURIComponent(courseId)}/consent`);
}
export async function sendPkConsentOtp(courseId) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/pk-courses/${encodeURIComponent(courseId)}/consent/send-otp`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Could not send OTP (${res.status})`);
  return data;
}
export async function verifyPkConsent({ courseId, otp }) {
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/pk-courses/${encodeURIComponent(courseId)}/consent/verify`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ otp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Verification failed (${res.status})`);
  return data;
}
